/**
 * @file Contributor UI — catalogRawMaterialResolution.
 * Map background_raw_materials rows to `raw_materials.id` (catalog FK for A1/A2 save).
 */

import axiosClient from "../../../../../api/axiosClient";
import { API_URLS } from "../../../../../constants/apiUrls";

/** Exported function — normalize Raw Material Name Key. */
export function normalizeRawMaterialNameKey(name: string): string {
  return String(name ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** Exported function — extract Raw Materials Array From Response. */
export function extractRawMaterialsArrayFromResponse(res: {
  data?: unknown;
}): unknown[] {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (
    d &&
    typeof d === "object" &&
    Array.isArray((d as { data?: unknown[] }).data)
  ) {
    return (d as { data: unknown[] }).data;
  }
  return [];
}

/** Exported function — fetch Catalog Raw Materials For Material. */
export async function fetchCatalogRawMaterialsForMaterial(
  materialId: number
): Promise<{ id: number; name: string }[]> {
  const res = await axiosClient.get(
    API_URLS.RAW_MATERIALS.BY_MATERIAL(materialId)
  );
  return extractRawMaterialsArrayFromResponse(res)
    .map((rm) => {
      const r = rm as Record<string, unknown>;
      return {
        id: Number(r?.id),
        name: String(r?.name ?? "").trim(),
      };
    })
    .filter((row) => Number.isFinite(row.id) && row.id > 0);
}

/**
 * Never treat background junction ids as catalog ids unless they appear in `catalog`.
 */
export function resolveCatalogRawMaterialIdFromBackgroundRow(
  bgRow: Record<string, unknown>,
  catalog: { id: number; name: string }[]
): number | null {
  if (!catalog.length) return null;

  const catalogIdSet = new Set(catalog.map((c) => c.id));

  const nameRaw =
    bgRow?.rawMaterialName ??
    bgRow?.raw_material_name ??
    bgRow?.materialName ??
    bgRow?.material_name ??
    bgRow?.name ??
    "";
  const name =
    typeof nameRaw === "string" ? nameRaw.trim() : String(nameRaw ?? "");

  const candidates = [
    bgRow?.rawMaterialId,
    bgRow?.raw_material_id,
    bgRow?.materialId,
    bgRow?.material_id,
  ]
    .map((x) => Number(x))
    .filter((n) => Number.isFinite(n) && n > 0);

  for (const cand of candidates) {
    if (catalogIdSet.has(cand)) return cand;
  }

  if (name) {
    const k = normalizeRawMaterialNameKey(name);
    const exact = catalog.find(
      (c) => normalizeRawMaterialNameKey(c.name) === k
    );
    if (exact) return exact.id;

    const nl = name.toLowerCase();
    const loose = catalog.filter(
      (c) =>
        c.name.toLowerCase() === nl ||
        nl.includes(c.name.toLowerCase()) ||
        c.name.toLowerCase().includes(nl)
    );
    if (loose.length === 1) return loose[0].id;
  }

  return null;
}
